import { apiRequest } from "@/api";
import { ENDPOINTS } from "@/api/endpoints";
import { CURRENT_PUSH_NOTIFICATION_PROVIDER, PUSH_NOTIFICATION_PROVIDERS } from "../pwa.constants";
import { PWA_EVENTS } from "../pwa.events";
import type { INotificationProvider, NotificationProvider } from "../pwa.types";

type NotificationSubscriberOptions = {
  registration: ServiceWorkerRegistration;
  type: INotificationProvider;
};

export class NotificationSubscriber {
  subscribed: boolean = false;
  private registration: ServiceWorkerRegistration;
  private provider: NotificationProvider | undefined;
  private type: INotificationProvider;

  constructor(opts: NotificationSubscriberOptions) {
    this.registration = opts.registration;
    this.type = opts.type;
  }

  async init() {
    let provider: NotificationProvider | undefined;
    switch (this.type) {
      case PUSH_NOTIFICATION_PROVIDERS.Native: {
        const module = await import("./subscribe-to-native-provider");
        provider = new module.NotificationProviderNative();
        break;
      }
      case PUSH_NOTIFICATION_PROVIDERS.Fsm: {
        const module = await import("./subscribe-to-fcm-provider");
        provider = new module.NotificationProviderFCM();
        break;
      }
      default: {
      }
    }
    if (!provider) throw new Error("Provider is undefined");
    this.provider = provider;
  }

  async unsubscribe(forceUnsubscribe?: boolean) {
    if (!this.provider) throw new Error("required init provider");
    const subscription = await this.registration.pushManager.getSubscription();
    if (!subscription && !forceUnsubscribe) {
      PWA_EVENTS.NotificationSubscribed.next(false);
      return;
    }
    await unregister(subscription?.endpoint);
    await this.provider.unsubscribe(this.registration);
    PWA_EVENTS.NotificationSubscribed.next(false);
  }

  async subscribe(forceSubscribe?: boolean) {
    if (!this.provider) throw new Error("required init provider");
    let subscription: unknown = await this.registration.pushManager.getSubscription();
    if (subscription && !forceSubscribe) {
      PWA_EVENTS.NotificationSubscribed.next(true);
      return subscription;
    }
    const vapid = await getVapid();
    if (!vapid) throw new Error("vapid is empty");
    subscription = await this.provider.subscribe(vapid, this.registration);
    await register(subscription);
    this.subscribed = true;
    PWA_EVENTS.NotificationSubscribed.next(true);
  }

  async notify(forceNotification: boolean) {
    return notify(forceNotification);
  }
}

async function getVapid() {
  const [vapid, error] = await apiRequest<string>({
    method: "GET",
    path:
      CURRENT_PUSH_NOTIFICATION_PROVIDER === PUSH_NOTIFICATION_PROVIDERS.Native
        ? ENDPOINTS.notificationVapidPush
        : ENDPOINTS.notificationVapidFCM,
  });
  if (!error) {
    return vapid;
  }
  return null;
}

async function register(subscription: unknown) {
  const [result, error] = await apiRequest<string>({
    method: "POST",
    path:
      CURRENT_PUSH_NOTIFICATION_PROVIDER === PUSH_NOTIFICATION_PROVIDERS.Native
        ? ENDPOINTS.notificationRegisterPush
        : ENDPOINTS.notificationRegisterFCM,
    body: subscription,
  });
  if (!error) {
    return result;
  }
  return null;
}
async function unregister(endpoint?: string) {
  if (CURRENT_PUSH_NOTIFICATION_PROVIDER === PUSH_NOTIFICATION_PROVIDERS.Fsm) return null;
  if (!endpoint) return null;
  const [result, error] = await apiRequest<string>({
    method: "POST",
    path: ENDPOINTS.notificationUnregisterPush,
    body: { endpoint },
  });
  if (!error) {
    return result;
  }
  return null;
}

async function notify(forceNotification: boolean) {
  return apiRequest({
    method: "POST",
    path:
      CURRENT_PUSH_NOTIFICATION_PROVIDER === PUSH_NOTIFICATION_PROVIDERS.Native
        ? ENDPOINTS.notificationSendPush
        : ENDPOINTS.notificationSendFCM,
    // force - os, not force - in app
    queries: { force: forceNotification },
  });
}
